import { Button, buttonVariants } from '@/components/ui/button';
import { Pagination } from '@/types';
import { Link } from '@inertiajs/react';
import {
    ChevronLeft,
    ChevronRight,
    ChevronsLeft,
    ChevronsRight,
} from 'lucide-react';
import { ButtonGroup } from './ui/button-group';
import { cn } from '@/lib/utils';

interface Props {
    pagination: Pagination | null | undefined;
    baseUrl?: string;
    query?: Record<string, string | number | boolean | null | undefined>;
}

function buildUrl(
    baseUrl: string,
    page: number,
    query?: Record<string, string | number | boolean | null | undefined>,
) {
    const params = new URLSearchParams();
    params.set('page', String(page));

    if (query) {
        for (const [key, value] of Object.entries(query)) {
            if (value !== undefined && value !== null && key !== 'page') {
                params.set(key, String(value));
            }
        }
    }

    const qs = params.toString();
    return `${baseUrl}${qs ? `?${qs}` : ''}`;
}

export default function PaginationNav({
    pagination,
    baseUrl = '/',
    query,
}: Props) {
    if (!pagination) return null;

    const { current_page, last_page } = pagination;
    if (!current_page || !last_page || last_page <= 1) return null;

    const prevPage = Math.max(1, current_page - 1);
    const nextPage = Math.min(last_page, current_page + 1);

    let pages: number[] = [];

    if (current_page === 1) {
        // First page → show 1..5
        pages = Array.from({ length: Math.min(5, last_page) }, (_, i) => i + 1);
    } else if (current_page === last_page) {
        // Last page → show last 5
        const start = Math.max(1, last_page - 4);
        pages = Array.from(
            { length: last_page - start + 1 },
            (_, i) => start + i,
        );
    } else {
        // Middle pages → show prev, current, next
        pages = [current_page - 1, current_page, current_page + 1].filter(
            (p) => p >= 1 && p <= last_page,
        );
    }
    return (
        <nav className="px- container mt-6 flex justify-center md:justify-end">
            <ButtonGroup>
                {/* First page */}
                {!(current_page === 1) && (
                    <Link disabled={current_page === 1} prefetch className={cn(buttonVariants({ variant: 'outline', size: 'icon' }))} href={buildUrl(baseUrl, 1, query)}>
                        <ChevronsLeft className="h-4 w-4" />
                    </Link>
                )}

                {/* Previous */}
                {!(current_page === 1) && (
                    <Link disabled={current_page === 1} prefetch className={cn(buttonVariants({ variant: 'outline', size: 'icon' }))} href={buildUrl(baseUrl, prevPage, query)}>
                        <ChevronLeft className="h-4 w-4" />
                    </Link>
                )}

                {/* Page numbers */}
                {pages.map((p) => (
                    <Link key={p} prefetch className={cn(buttonVariants({ variant: p === current_page ? 'default' : 'outline', size: 'icon' }))} href={buildUrl(baseUrl, p, query)}>{p}</Link>
                ))}

                {/* Next */}
                {!(current_page === last_page) && (
                    <Link disabled={current_page === last_page} prefetch className={cn(buttonVariants({ variant: 'outline', size: 'icon' }))} href={buildUrl(baseUrl, nextPage, query)}>
                        <ChevronRight className="h-4 w-4" />
                    </Link>
                )}

                {/* Last page */}
                {!(current_page === last_page) && (
                    <Link disabled={current_page === last_page} prefetch className={cn(buttonVariants({ variant: 'outline', size: 'icon' }))} href={buildUrl(baseUrl, last_page, query)}>
                        <ChevronsRight className="h-4 w-4" />
                    </Link>
                )}
            </ButtonGroup>
        </nav>
    );
}
